import React, { useCallback } from 'react';
import InputBase from '../InputBase';
import styled from 'styled-components/macro';
import FieldTitle from '../FieldTitle';
import formatDate from '../../../utils/formatDate';
import PhoneFieldEngine, {
  phoneFieldValidator
} from '../engine/PhoneFieldEngine';
import DateFieldEngine from '../engine/DateFieldEngine';
import DropDownFieldEngine from '../engine/DropDownFieldEngine';
import ConfirmationBoxEngine from '../engine/ConfirmationBoxEngine';
import { useField } from '../FormContext';
import useTranslations from '../../../hooks/useTranslations';
import EmailFieldEngine, {
  emailFieldValidator
} from '../engine/EmailFieldEngine';
import Description from '../Description';
import MultiSelectFieldEngine from '../engine/MultiSelectFieldEngine';

const SubFieldsContainer = styled.div`
  margin: 0 0 0 2rem;
`;

const LineItem = styled.div`
  border-left: 1px solid #c4c4c4 !important;
  left: -1.5rem !important;
  position: absolute;
  width: 1rem;
  top: ${props => (props.first ? '0.5rem' : '-2.5rem')};
  bottom: 14px;
  border-bottom: 1px solid #c4c4c4 !important;
  .EUF-error && {
    bottom: 2.5rem;
  }
`;

const ErrorMsg = styled.p`
  padding: 0;
  font-size: 14px;
`;

const AMLSubField = ({
  data,
  index,
  hiddenSubFields = [],
  documentIdTypeOptions,
  defaultThirdfortCheckTypes,
  showAdminFields,
  subFieldValue,
  setSubFieldValue,
  onClickSubField,
  onFocus,
  onBlur,
  parentUIType,
  validateDocFields,
  checkIfRequiredByFormLogic = () => false
}) => {
  const t = useTranslations();
  const validator = useCallback(
    required => value => {
      value = value || value === 0 ? value : '';
      value += '';
      let error = '';

      if (required && value.trim() === '') {
        error = t('forms.fields.error.required');
      }

      if (
        ['documentId', 'documentIdType', 'documentIdCountry'].indexOf(
          data.internalId
        ) !== -1 &&
        !value &&
        !validateDocFields()
      ) {
        error = t(`forms.fields.error.${data.internalId}`);
      }

      if (
        data.internalId === 'transactionName' &&
        (!value || ('' + value).length < 3)
      ) {
        error = t(`forms.fields.error.short_transaction_name`);
      }

      return error;
    },
    [validateDocFields]
  );
  let displayValue, readOnly, defaultValue;
  displayValue = subFieldValue || '';
  readOnly = !data.lookupInfo.subFieldInfo.editable;
  if (data.uiType === 'date' && subFieldValue) {
    displayValue = formatDate(subFieldValue, null, 'L');
  }
  defaultValue =
    data.defaultValue &&
    data.defaultValue.properties &&
    data.defaultValue.properties.value
      ? data.defaultValue.properties.value
      : undefined;
  if (
    parentUIType === 'thirdfort' &&
    defaultThirdfortCheckTypes &&
    data.internalId === 'checkTypes'
  ) {
    defaultValue = defaultThirdfortCheckTypes;
  }
  if (
    data.lookupInfo &&
    data.lookupInfo.subFieldInfo &&
    data.uiType === 'phone'
  ) {
    useField(
      data.localFormFieldId,
      phoneFieldValidator(
        t,
        data.required || checkIfRequiredByFormLogic('phone')
      )
    );
  } else if (
    data.lookupInfo &&
    data.lookupInfo.subFieldInfo &&
    data.uiType === 'email_address'
  ) {
    useField(
      data.localFormFieldId,
      emailFieldValidator(
        t,
        data.required || checkIfRequiredByFormLogic('email')
      )
    );
  } else {
    useField(
      data.localFormFieldId,
      validator(data.required || checkIfRequiredByFormLogic(data.internalId))
    );
  }

  const CREDAS_ADMIN_ONLY_SUBFIELDS = [
    'status',
    'date',
    'loe',
    'addressMatch',
    'loeStatus',
    'title',
    'contactVia',
    'runAgain'
  ];

  const isCredasAdminField =
    parentUIType === 'credas' &&
    CREDAS_ADMIN_ONLY_SUBFIELDS.includes(data.internalId);

  const shouldBeHidden =
    hiddenSubFields.includes(data.internalId) ||
    (!showAdminFields &&
      (parentUIType == 'worldcheck' || parentUIType === 'worldcheck_org') &&
      (data.internalId === 'status' || data.internalId === 'date')) ||
    (!showAdminFields && isCredasAdminField);

  return (
    <div
      field-id={data.localFormFieldId}
      value={subFieldValue || defaultValue}
      style={{
        position: 'relative',
        height: shouldBeHidden ? 0 : '',
        opacity: shouldBeHidden ? 0 : '',
        backgroundColor: isCredasAdminField ? 'rgba(76, 132, 255, 0.1)' : ''
      }}
      onClick={onClickSubField}
    >
      <LineItem first={index === 0} />
      <FieldTitle
        title={data.title}
        isReadOnly={readOnly}
        isRequired={
          data.required || checkIfRequiredByFormLogic(data.internalId)
        }
      />
      {data.description ? (
        <Description fontSize={14} content={data.description} />
      ) : (
        ''
      )}
      {data.uiType === 'phone' ? (
        <PhoneFieldEngine
          defaultCountry="gb"
          disabled={readOnly}
          internalId={data.localFormFieldId}
          options={data.options}
          variant="outlined"
          value={displayValue}
          inputProps={{ readOnly: readOnly }}
          onChange={v => setSubFieldValue(index, v)}
          onFocus={() => {
            onFocus(index);
          }}
          onBlur={() => {
            onBlur(index);
          }}
        />
      ) : data.uiType === 'dropdown' ? (
        <DropDownFieldEngine
          disabled={readOnly}
          internalId={data.localFormFieldId}
          setValue={v => setSubFieldValue(index, v)}
          value={subFieldValue || defaultValue}
          inputProps={{ readOnly: readOnly }}
          options={
            data.internalId === 'documentIdType'
              ? documentIdTypeOptions
              : data.options || []
          }
          onFocus={() => {
            onFocus(index);
          }}
          onBlur={() => {
            onBlur(index);
          }}
        />
      ) : data.uiType === 'multiple_choice' ? (
        <MultiSelectFieldEngine
          readOnly={readOnly}
          internalId={data.localFormFieldId}
          options={data.options}
          setValue={v => setSubFieldValue(index, v)}
          value={subFieldValue || defaultValue}
          inputProps={{ readOnly: readOnly }}
          onFocus={() => {
            onFocus(index);
          }}
          onBlur={() => {
            onBlur(index);
          }}
        />
      ) : data.uiType === 'date' ? (
        <DateFieldEngine
          readOnly={readOnly}
          internalId={data.localFormFieldId}
          setValue={v => setSubFieldValue(index, v)}
          value={subFieldValue}
          inputProps={{ readOnly: readOnly }}
          onFocus={() => {
            onFocus(index);
          }}
          onBlur={() => {
            onBlur(index);
          }}
          disableFuture={data.internalId === 'dateOfBirth'}
        />
      ) : data.uiType === 'email_address' ? (
        <EmailFieldEngine
          readOnly={readOnly}
          internalId={data.localFormFieldId}
          value={displayValue}
          inputProps={{ readOnly: readOnly }}
          setValue={v => setSubFieldValue(index, v)}
          onFocus={() => {
            onFocus(index);
          }}
          onBlur={() => {
            onBlur(index);
          }}
        />
      ) : data.uiType === 'confirmation' ? (
        <ConfirmationBoxEngine
          readOnly={readOnly}
          internalId={data.localFormFieldId}
          options={data.options}
          value={displayValue}
          inputProps={{ readOnly: readOnly }}
          setValue={v => setSubFieldValue(index, v)}
          onFocus={() => {
            onFocus(index);
          }}
          onBlur={() => {
            onBlur(index);
          }}
        />
      ) : (
        <InputBase
          disabled={readOnly}
          variant="outlined"
          value={displayValue}
          inputProps={{ readOnly: readOnly }}
          onChange={e => setSubFieldValue(index, e.target.value)}
          onFocus={() => {
            onFocus(index);
          }}
          onBlur={() => {
            onBlur(index);
          }}
        />
      )}
      <ErrorMsg $uiType={data.uiType} data-error={data.internalId} />
    </div>
  );
};

const AMLFieldView = props => {
  return (
    <div>
      <SubFieldsContainer>
        {props.subFields.map((one, k) => (
          <AMLSubField
            key={k + props.defaultThirdfortCheckTypes}
            index={k}
            data={one}
            parentUIType={props.uiType}
            hiddenSubFields={props.hiddenSubFields}
            subFieldValue={props.subFieldValues[k]}
            showAdminFields={props.showAdminFields}
            setSubFieldValue={props.setSubFieldValue}
            onClickSubField={props.onClickSubField}
            onFocus={props.onSubFieldFocus}
            onBlur={props.onSubFieldBlur}
            documentIdTypeOptions={props.documentIdTypeOptions}
            defaultThirdfortCheckTypes={props.defaultThirdfortCheckTypes}
            validateDocFields={props.validateDocFields}
            checkIfRequiredByFormLogic={props.checkIfRequiredByFormLogic}
          />
        ))}
      </SubFieldsContainer>
    </div>
  );
};

export default AMLFieldView;
